import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';



const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!email.trim()) {
            toast.error("Please enter your email address");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error("Please enter a valid email address");
            return;
        }

        setLoading(true);
        const loadingToastId = toast.loading("Sending reset link...");

        try {
            const response = await 
                // axios.post('/api/users/forgot-password', { email });
            axios.post(`${import.meta.env.VITE_BACKEND_URL}/api/users/forgot-password`, { email });

            
            toast.dismiss(loadingToastId);
            toast.success(response.data.message || "Reset link sent successfully!");
            setEmailSent(true);
        } catch (error) {
            toast.dismiss(loadingToastId);
            toast.error(error.response?.data?.message || "Failed to send reset link. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (emailSent) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <div className="flex justify-center mb-8">
                        <img 
                            src="https://res.cloudinary.com/dmrlotfsv/image/upload/v1754071341/VESHH-EON_TYPE_WEB_ppfyxy.png" 
                            className="h-16 w-auto object-contain"
                            alt="VESHH-EON Logo"
                        />
                    </div>

                    {/* Success Card */}
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-10">
                        <div className="text-center">
                            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                                <CheckCircle className="w-10 h-10 text-green-600" />
                            </div>
                            
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                Check Your Email
                            </h2>
                            
                            <p className="text-gray-600 mb-6">
                                We've sent a password reset link to <strong>{email}</strong>
                            </p>
                            
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                <p className="text-sm text-blue-900">
                                    <strong>Didn't receive the email?</strong>
                                    <br />
                                    Check your spam folder or click the button below to resend.
                                </p>
                            </div>

                            <button
                                onClick={() => {
                                    setEmailSent(false);
                                    handleSubmit({ preventDefault: () => {} });
                                }}
                                className="w-full bg-black text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-200 mb-4"
                            >
                                Resend Email
                            </button>

                            <Link
                                to="/login"
                                className="flex items-center justify-center text-sm text-gray-600 hover:text-black transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen">
            {/* Left Side - Forgot Password Form */}
            <div className="w-full md:w-1/2 flex flex-col justify-center items-center p-8 md:p-12 bg-gray-50">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <div className="flex justify-center mb-8">
                        <img 
                            src="https://res.cloudinary.com/dmrlotfsv/image/upload/v1754071341/VESHH-EON_TYPE_WEB_ppfyxy.png" 
                            className="h-16 w-auto object-contain"
                            alt="VESHH-EON Logo"
                        />
                    </div>

                    {/* Form Container */}
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-10">
                        {/* Header */}
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                Forgot Password?
                            </h2>
                            <p className="text-gray-600">
                                No worries! Enter your email and we'll send you reset instructions.
                            </p>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Email Field */}
                            <div>
                                <label 
                                    className="block text-gray-700 text-sm font-semibold mb-2" 
                                    htmlFor="email"
                                >
                                    Email Address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-2 focus:ring-black/10 transition-all duration-200"
                                        placeholder="your@email.com"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button 
                                type="submit" 
                                disabled={loading}
                                className="w-full bg-black text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-black/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </span>
                                ) : (
                                    "Send Reset Link"
                                )}
                            </button>
                        </form>

                        {/* Back to Login Link */}
                        <div className="mt-8 text-center">
                            <Link
                                to="/login"
                                className="inline-flex items-center text-sm text-gray-600 hover:text-black transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to Login
                            </Link>
                        </div>
                    </div>

                    {/* Help Text */}
                    <div className="mt-8 text-center">
                        <p className="text-xs text-gray-500">
                            Need help? Contact us at{' '}
                            <a href="mailto:support@veshheon.in" className="underline hover:text-gray-700">
                                support@veshheon.in
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            {/* Right Side - Illustration */}
            <div className="hidden md:flex w-1/2 bg-black relative overflow-hidden items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-br from-black/50 to-transparent z-10"></div>
                <div className="z-20 text-white max-w-md px-12 text-center">
                    <div className="mb-8">
                        <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Mail className="w-12 h-12 text-white" />
                        </div>
                    </div>
                    <h3 className="text-3xl font-bold mb-4">
                        Password Recovery
                    </h3>
                    <p className="text-gray-200 text-lg">
                        We'll send you a secure link to reset your password and get you back to shopping in no time.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
